import parse from "html-react-parser";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  ReactNode,
  memo,
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { InlineInfoBox } from "staysure-component-library";

import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import FadeAnimation from "@/components/atoms/animation/fade-animation";
import TransformAnimation from "@/components/atoms/animation/transform-animation";
import ButtonGroup from "@/components/atoms/button-group/button-group";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import ActionButton from "@/components/molecules/action-button/action-button";
import ConfirmationsModal from "@/components/molecules/quote-section/edit-quote-section/confirmation-modal";
import BackToRenewal from "@/components/molecules/renewals/back-to-renewal/back-to-renewal";
import { MedicalConfirmationFlowEnum } from "@/components/with-undiagnosed-symptoms";
import {
  MedicalEdit,
  medicalUsagePageQuestionTypes,
  OrganizerEdit,
  QuestionMedical,
  QuestionMedicalConfirmation,
  QuestionMedicalConfirmationv2,
  QuestionMedicalEligibility,
  QuestionMedicalTreatments,
  RenewalPage,
  TravellerEdit,
} from "@/config/app-constant";
import { useStickyFooter } from "@/hooks/use-media-query/use-media-query-enhanced";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useAppDispatch, useAppSelector } from "@/store/hook";
import { RootState } from "@/store/root-reducer";
import { MedicalState, setMedicalData } from "@/store/slices/medical.slice";
import { isMedicalEligibilityFlow } from "@/utils/medical.utils";
import { handleScrollToRef } from "@/utils/scrollUtils";

import styles from "./q-medical-section.module.css";
import { answers, findAnswerById } from "./q-medical.data";
import { QMedicalContainerProps } from "./q-medical.types";
import editQuoteStyle from "../../../molecules/quote-section/edit-quote-section/edit-quote.module.css";
import UnSavedChangesWarningMessage from "../../un-saved-changes-warning-message/un-saved-changes-warning-message";

function QMedicalContainer({
  children,
  questionText = "Question Text",
  questionInfoText = "Question Info Text",
  questionInfoTextWithUndiagnosed = "Question Info Text Variation",
  questionInfoTextWithUndiagnosedOptimizelyTestEnabled = "Question Info Text Variation Optimizely Test Enabled",
  questionUndiagnosedText = "Undiagnosed Question Text",
  questionUndiagnosedInfoText = "Undiagnosed Question Info Text",
  ctaButtonText = "Button Text",
  backToRenewalBtnText,
  nonRoutineTestInfoMessage,
  usagePage,
  ctaRef,
  formRef,
  selectedAnswer,
  questionTwoSelectedAnswer,
  yesCtaButtonText,
  noCtaButtonText,
  yesUndiagnosedCtaButtonText,
  noUndiagnosedCtaButtonText,
  setSelectedAnswer,
  setQuestionTwoAnswer,
  onSubmit,
  onBack,
  ctnBtnLoading,
  removeMedicalConditionModalTitle,
  removeMedicalConditionModalDescription,
  removeMedicalConditionModalDescriptionRenewal,
  removeMedicalConditionModalCTABtnOne,
  removeMedicalConditionModalCTABtnTwo,
  isTravelerHasMedicalCondition = false,
  saveQuestionModalTitle,
  saveQuestionModalDescription,
  saveQuestionModalCTABtnOne,
  saveQuestionModalCTABtnTwo,
  finishEditingBtnText = "Finish Editing",
  newMedicalFlowEnabled,
  medicalConfirmationFlow,
  application,
  showInforBox,
}: Readonly<QMedicalContainerProps & { children?: ReactNode }>) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { t } = useWizardTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const medicalDetails = useAppSelector((state: RootState) => state.medical);
  const [medicalConditionModalOpen, setMedicalConditionModalOpen] =
    useState(false);
  const travelerEdit = searchParams.get(TravellerEdit);
  const organizerEdit = searchParams.get(OrganizerEdit);
  const medicalEdit = searchParams.get(MedicalEdit);
  const editPage =
    searchParams.get(MedicalEdit) || travelerEdit || organizerEdit;
  const { attributes } = useAppSelector((state: RootState) => state.quote);
  const [
    openSaveDetailsConfirmationModal,
    setOpenSaveDetailsConfirmationModal,
  ] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const undiagnosedQRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const { resetQuestionData } = useResetStateTravelerEdit();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [CTAButtonName, setCTAButtonName] = useState(ctaButtonText);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState(false);
  const isStickyFooter = useStickyFooter();
  const actionButtonRef = useRef<HTMLDivElement | null>(null);
  const { current } = useWizard();

  useEffect(() => {
    const checkCTABtnDisabled = () => {
      if (
        !Object.keys(medicalDetails).includes(
          medicalUsagePageQuestionTypes[usagePage]
        )
      ) {
        setButtonDisabled(true);
      } else if (
        medicalDetails[medicalUsagePageQuestionTypes[usagePage]] === null
      ) {
        setButtonDisabled(true);
      } else if (
        usagePage === QuestionMedicalConfirmation &&
        medicalDetails.agreedConfirmation === false
      ) {
        setButtonDisabled(true);
      } else if (
        usagePage === QuestionMedicalConfirmation &&
        medicalDetails.agreedConfirmation === true &&
        medicalDetails.hasUndiagnosedSymptoms === null
      ) {
        setButtonDisabled(true);
      } else if (
        usagePage === QuestionMedicalEligibility &&
        medicalDetails.agreedEligibility === false
      ) {
        setButtonDisabled(true);
      } else {
        setButtonDisabled(false);
      }
    };

    checkCTABtnDisabled();
  }, [medicalDetails, usagePage]);

  useEffect(() => {
    if (
      (editPage || travelerEdit || organizerEdit) &&
      usagePage === QuestionMedicalTreatments &&
      medicalDetails.agreedConfirmation &&
      (medicalDetails.agreedHistory === false || newMedicalFlowEnabled) &&
      medicalDetails.agreedTreatments === false
    ) {
      setCTAButtonName(finishEditingBtnText);
    } else {
      setCTAButtonName(ctaButtonText);
    }
  }, [
    ctaButtonText,
    editPage,
    finishEditingBtnText,
    medicalDetails.agreedConfirmation,
    medicalDetails.agreedHistory,
    newMedicalFlowEnabled,
    medicalDetails.agreedTreatments,
    organizerEdit,
    travelerEdit,
    usagePage,
  ]);

  const isTravelerHasMedicalConditionValue = useMemo(() => {
    return isTravelerHasMedicalCondition ?? false;
  }, [isTravelerHasMedicalCondition]);

  const handleSubmitButton = () => {
    if (
      usagePage === QuestionMedicalTreatments &&
      medicalDetails.agreedConfirmation &&
      (medicalDetails.agreedHistory === false || newMedicalFlowEnabled) &&
      medicalDetails.agreedTreatments === false &&
      !medicalConditionModalOpen &&
      (isTravelerHasMedicalConditionValue ||
        sessionStorage.getItem("isPrevMedicalAvailable") === "true")
    ) {
      setMedicalConditionModalOpen(true);
    } else if (
      usagePage === QuestionMedicalTreatments &&
      (medicalDetails.agreedHistory ||
        medicalDetails.agreedTreatments ||
        sessionStorage.getItem("isPrevMedicalAvailable") === "true") &&
      (editPage || travelerEdit || organizerEdit)
    ) {
      setOpenSaveDetailsConfirmationModal(true);
    } else if (onSubmit && !buttonDisabled) {
      onSubmit(usagePage);
    }
  };

  const handleBackButton = () => {
    if (
      application === "renewal" &&
      medicalEdit === "true" &&
      (usagePage === QuestionMedicalConfirmationv2 ||
        usagePage === QuestionMedicalConfirmation)
    ) {
      setIsOpenUnsavedChangesModal(true);
    } else if (onBack) {
      onBack(usagePage);
    }
  };

  const revertCurrentChanges = useCallback(() => {
    dispatch(
      setMedicalData(
        attributes?.travellerDetails?.gateKeeperAnswers
          ? attributes?.travellerDetails?.gateKeeperAnswers
          : ({} as MedicalState)
      )
    );
    if (travelerEdit) {
      resetQuestionData();
    }
  }, [
    attributes?.travellerDetails?.gateKeeperAnswers,
    dispatch,
    resetQuestionData,
    travelerEdit,
  ]);

  const handleCancelEdit = () => {
    setModalOpen(true);
  };

  const renderCancelBtn = () => {
    return (
      <div>
        <LabelButtonWrapper
          hierarchy="tertiary"
          label={t("cancel_editing", "Cancel editing")}
          id={`cancel-edit-${usagePage}`}
          className={`${editQuoteStyle.cancelEditButton}`}
          onClick={handleCancelEdit}
        />
        <ConfirmationsModal
          isOpen={modalOpen}
          handleConfirmBtnClick={() => {
            // Check if edit originated from quote-review page
            const editSection = getQuoteReviewSummaryEditSection();
            if (editSection) {
              // Navigate back to quote-review page
              router.push(current.edges.cancelQuoteReviewSummaryEdit ?? "");
            } else {
              // Use default cancelEdit edge (navigate to quote page)
              router.push(current.edges?.cancelEdit ?? "");
            }
            revertCurrentChanges();
          }}
          handleCancelmBtnClick={() => {
            setModalOpen(false);
          }}
        />
      </div>
    );
  };

  const handleUnSavedWarningMessageContinueBtn = useCallback(() => {
    // Revert the current made changes and redirect to the home page
    revertCurrentChanges();
    setIsOpenUnsavedChangesModal(false);
    router.push("/");
  }, [revertCurrentChanges, router]);

  const renderBackToRenewalButton = useMemo(() => {
    return (
      <div>
        <BackToRenewal
          label={backToRenewalBtnText ?? ""}
          className="pb-[0px] 
    top-[-2px] !px-[0px] mb-spacing-l md:!mb-spacing-l lg:mx-[0px]"
          onClick={() => setIsOpenUnsavedChangesModal(true)}
        />
      </div>
    );
  }, [backToRenewalBtnText]);

  // Render Unsaved changes modal
  const renderUnsavedChangesModal = useMemo(() => {
    return (
      <UnSavedChangesWarningMessage
        isOpen={isOpenUnsavedChangesModal}
        onContinueClick={handleUnSavedWarningMessageContinueBtn}
        setIsOpen={setIsOpenUnsavedChangesModal}
      />
    );
  }, [handleUnSavedWarningMessageContinueBtn, isOpenUnsavedChangesModal]);
  const showUndiagnosedQuestion = useMemo(
    () =>
      usagePage === QuestionMedicalConfirmation &&
      medicalConfirmationFlow ===
        MedicalConfirmationFlowEnum.withUndiagnosedSymptomsQuestion,
    [usagePage, medicalConfirmationFlow]
  );

  const handleSelectedAnswer = (answerNo: number) => {
    setSelectedAnswer(answerNo);
    // setTimeout(() => {
    //   if (showUndiagnosedQuestion) {
    //     if (answerNo === 1) {
    //       ref.current?.scrollIntoView({ behavior: "smooth" });
    //     }
    //     undiagnosedQRef.current?.scrollIntoView({ behavior: "smooth" });
    //   } else {
    //     ref.current?.scrollIntoView({ behavior: "smooth" });
    //   }
    // }, 200);
  };

  const handleQuestionTwoSelectedAnswer = (answerNo: number) => {
    setQuestionTwoAnswer(answerNo);
    // setTimeout(() => {
    //   ref.current?.scrollIntoView({ behavior: "smooth" });
    // }, 200);
  };

  const renderMedicalSection = () => {
    const selectedTypeId = selectedAnswer
      ? findAnswerById(selectedAnswer)?.id
      : null;

    const questionTwoSelectedTypeId = questionTwoSelectedAnswer
      ? findAnswerById(questionTwoSelectedAnswer)?.id
      : null;
    const getQuestionInfoText = () => {
      if (showUndiagnosedQuestion && isMedicalEligibilityFlow()) {
        return questionInfoTextWithUndiagnosedOptimizelyTestEnabled;
      }
      if (showUndiagnosedQuestion) {
        return questionInfoTextWithUndiagnosed;
      }
      return questionInfoText;
    };

    return (
      <div className="">
        {" "}
        <div>
          {editPage && application === "quote" && renderCancelBtn()}
          {editPage && application === "renewal" && renderBackToRenewalButton}
          <RichText
            richText={questionText}
            className=" font-header-font lg:text-[39px]  tablet:text-[31px] md:text-3xl text-[26px] lg:leading-[44.85px] 
                md:leading-[34.1px] tablet:leading-[34.1px] leading-[29.9px] text-text-title text-left"
          />
        </div>
        <span
          className={`${styles.richTextContainer} block mt-4 text-xs [&_ul]:pl-8 [&_li]:list-disc [&_li]:leading-[27px] [&_li]:mb-3 md:mt-6 text-text-body`}
        >
          {parse(getQuestionInfoText())}
        </span>
        {nonRoutineTestInfoMessage &&
          current?.uiElementControls?.showNonRoutineTestInfoMessage && (
            <InlineInfoBox
              id="question-details-info-box-id"
              className="mt-3 border-l-8 rounded-md !border-border-info-primary bg-surface-info-2 text-surface-neutral-option-10 py-2 px-4"
              content={parse(nonRoutineTestInfoMessage ?? "") as string}
            />
          )}
        <ButtonGroup
          selectedAnswer={selectedTypeId}
          answerArray={[
            { ...answers[0], label: yesCtaButtonText ?? "Yes" },
            { ...answers[1], label: noCtaButtonText ?? "No" },
          ]}
          location={QuestionMedical}
          setSelectedAnswer={handleSelectedAnswer}
          type={`${usagePage}`}
          onOptionClick={(id, dataTestId) => {
            const buttonElement = document.querySelector(`#${dataTestId}`);
            if (
              buttonElement?.classList.contains(
                "componnt_lib_toggle_btn_inactive"
              )
            ) {
              setTimeout(() => {
                if (showUndiagnosedQuestion) {
                  if (id === 1) {
                    // handleScrollToRef(actionButtonRef, !isStickyFooter);
                    showInforBox?.();
                  }
                  undiagnosedQRef.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                } else {
                  handleScrollToRef(actionButtonRef, !isStickyFooter);
                }
              }, 200);
            }
          }}
        />
        {showUndiagnosedQuestion && selectedTypeId === answers[1].id && (
          <>
            <div ref={undiagnosedQRef}>
              <RichText
                richText={questionUndiagnosedText}
                className=" font-header-font lg:text-[39px]  tablet:text-[31px] md:text-3xl text-[26px] lg:leading-[44.85px] 
                md:leading-[34.1px] tablet:leading-[34.1px] leading-[29.9px] text-text-title text-left mt-4 md:mt-9"
              />
            </div>
            <span
              className={`${styles.richTextContainer} block mt-4 text-xs [&_ul]:pl-8 [&_li]:list-disc [&_li]:leading-[27px] [&_li]:mb-3 md:mt-6 text-text-body`}
            >
              {parse(questionUndiagnosedInfoText)}
            </span>
            <ButtonGroup
              selectedAnswer={questionTwoSelectedTypeId}
              answerArray={[
                { ...answers[0], label: yesUndiagnosedCtaButtonText ?? "Yes" },
                {
                  ...answers[1],
                  label: noUndiagnosedCtaButtonText ?? "No",
                  value: false,
                },
              ]}
              location={QuestionMedical}
              setSelectedAnswer={handleQuestionTwoSelectedAnswer}
              type={`undiagnosed-${usagePage}`}
              onOptionClick={(id, dataTestId) => {
                const buttonElement = document.querySelector(`#${dataTestId}`);
                if (
                  buttonElement?.classList.contains(
                    "componnt_lib_toggle_btn_inactive"
                  )
                ) {
                  if (id === 2) {
                    handleScrollToRef(actionButtonRef, !isStickyFooter);
                  } else {
                    showInforBox?.();
                  }
                }
              }}
            />
          </>
        )}
      </div>
    );
  };

  const confirmationModalForSavingDataInEditJourney = useMemo(() => {
    return (
      <ConfirmationsModal
        isOpen={openSaveDetailsConfirmationModal}
        handleConfirmBtnClick={() => {
          setOpenSaveDetailsConfirmationModal(false);
          if (onSubmit && !buttonDisabled) {
            onSubmit(usagePage);
          }
        }}
        handleCancelmBtnClick={() => {
          setOpenSaveDetailsConfirmationModal(false);
        }}
        modalTitle={saveQuestionModalTitle}
        modalDescription={saveQuestionModalDescription}
        ctaButtonLabel={saveQuestionModalCTABtnOne}
        backButtonLabel={saveQuestionModalCTABtnTwo}
      />
    );
  }, [
    openSaveDetailsConfirmationModal,
    saveQuestionModalTitle,
    saveQuestionModalDescription,
    saveQuestionModalCTABtnOne,
    saveQuestionModalCTABtnTwo,
    onSubmit,
    buttonDisabled,
    usagePage,
  ]);

  // medical condition removal modal

  const medicalConditionRemovalModal = useMemo(() => {
    return (
      <ConfirmationsModal
        isOpen={medicalConditionModalOpen}
        handleConfirmBtnClick={() => {
          setMedicalConditionModalOpen(false);

          if (
            (medicalDetails.agreedHistory || medicalDetails.agreedTreatments) &&
            (editPage || travelerEdit || organizerEdit)
          ) {
            setOpenSaveDetailsConfirmationModal(true);
          } else if (onSubmit && !buttonDisabled) {
            onSubmit(usagePage);
          }
        }}
        handleCancelmBtnClick={() => {
          setMedicalConditionModalOpen(false);
        }}
        modalTitle={removeMedicalConditionModalTitle}
        modalDescription={
          application === RenewalPage
            ? removeMedicalConditionModalDescriptionRenewal
            : removeMedicalConditionModalDescription
        }
        ctaButtonLabel={removeMedicalConditionModalCTABtnOne}
        backButtonLabel={removeMedicalConditionModalCTABtnTwo}
        isRichTextAvailable={application === RenewalPage}
      />
    );
  }, [
    medicalConditionModalOpen,
    removeMedicalConditionModalTitle,
    application,
    removeMedicalConditionModalDescriptionRenewal,
    removeMedicalConditionModalDescription,
    removeMedicalConditionModalCTABtnOne,
    removeMedicalConditionModalCTABtnTwo,
    medicalDetails.agreedHistory,
    medicalDetails.agreedTreatments,
    editPage,
    travelerEdit,
    organizerEdit,
    onSubmit,
    buttonDisabled,
    usagePage,
    router,
    current.edges.cancelQuoteReviewSummaryEdit,
  ]);

  return (
    <div
      data-testid="question-medical-test-id"
      className={`rounded-radius-xxl 
      bg-surface-neutral-option-1 rounded-b-radius-xxl  
           md:py-spacing-xxl py-spacing-m px-spacing-m h-full`}
    >
      <div className="lg:w-[414px] md:w-[414px] w-full mx-auto relative h-full flex flex-col justify-between">
        <TransformAnimation animationRef={formRef} currentPage={usagePage}>
          {renderMedicalSection()}
          {medicalConditionRemovalModal}
          {confirmationModalForSavingDataInEditJourney}
          {renderUnsavedChangesModal}
          {children}
        </TransformAnimation>
        <FadeAnimation animationRef={ctaRef}>
          <div ref={actionButtonRef}>
            <ActionButton
              page={usagePage}
              ctaButtonText={CTAButtonName}
              disableMain={buttonDisabled}
              submitButtonClick={handleSubmitButton}
              backButtonClick={handleBackButton}
              isLoading={ctnBtnLoading}
            />
          </div>
        </FadeAnimation>
        {/*  Taking a space from DOM to point the scroll */}
        <div ref={ref} className="invisible">
          {" "}
        </div>
      </div>
    </div>
  );
}
export default memo(QMedicalContainer);
